import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { comics } from '@/mocks/comics';

const chapterContentImages = [
  'https://readdy.ai/api/search-image?query=Chinese%20manhua%20comic%20page%20in%20black%20and%20white%20ink%20style%2C%20a%20young%20shopkeeper%20standing%20behind%20wooden%20counter%20in%20a%20cozy%20grocery%20store%2C%20various%20items%20on%20shelves%2C%20warm%20atmosphere%2C%20detailed%20linework%2C%20manga%20style%20panel%20layout&width=900&height=1300&seq=chapter-page-01&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Black%20and%20white%20manga%20comic%20page%2C%20a%20customer%20entering%20a%20vintage%20store%2C%20dialogue%20bubbles%20in%20Chinese%2C%20detailed%20background%20with%20shelves%20and%20products%2C%20expressive%20character%20faces%2C%20clean%20ink%20linework&width=900&height=1300&seq=chapter-page-02&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Manga%20comic%20page%20black%20and%20white%2C%20two%20characters%20having%20conversation%20in%20a%20cozy%20shop%20interior%2C%20warm%20expressions%2C%20detailed%20background%20with%20antique%20items%2C%20Chinese%20manhua%20style%20panels%20with%20clean%20inking&width=900&height=1300&seq=chapter-page-03&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Black%20and%20white%20comic%20page%2C%20close-up%20of%20a%20mysterious%20item%20glowing%20on%20a%20wooden%20table%2C%20character%20reaching%20out%20to%20touch%20it%2C%20dramatic%20lighting%20effect%20in%20ink%2C%20Chinese%20manhua%20style&width=900&height=1300&seq=chapter-page-04&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Manga%20page%20black%20and%20white%2C%20flashback%20scene%20of%20a%20rainy%20day%20outside%20a%20shop%2C%20character%20holding%20an%20umbrella%2C%20atmospheric%20ink%20wash%20background%2C%20emotional%20expression%2C%20Chinese%20comic%20style&width=900&height=1300&seq=chapter-page-05&orientation=portrait',
];

export default function ChapterPage() {
  const { slug, chapterNum } = useParams<{ slug: string; chapterNum: string }>();
  const comic = comics.find((c) => c.slug === slug);
  const chNum = parseInt(chapterNum || '1', 10);
  const chapter = comic?.chapters.find((c) => c.num === chNum);

  if (!comic || !chapter) {
    return (
      <>
        <Header />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <p className="text-lg text-foreground-700 mb-4">未找到该章节</p>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const totalChapters = comic.chapters.length;
  const hasPrev = chNum > 1;
  const hasNext = chNum < totalChapters;

  return (
    <>
      <Header />
      <main className="pt-16 bg-background-100">
        <div className="max-w-[900px] mx-auto px-4 py-4">
          <nav className="text-xs text-foreground-500 mb-4" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <span className="mx-2">/</span>
            <Link to={`/comic/${comic.slug}`} className="hover:text-primary-600 transition-colors">
              {comic.title}
            </Link>
            <span className="mx-2">/</span>
            <span className="text-foreground-700">{chapter.title}</span>
          </nav>

          <div className="bg-background-50 rounded-lg p-4 mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <h1 className="text-base font-heading font-bold text-foreground-950">
                {comic.title}
              </h1>
              <p className="text-sm text-foreground-600 mt-0.5">
                {chapter.title}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {hasPrev && (
                <Link
                  to={`/comic/${comic.slug}/chapter/${chNum - 1}`}
                  className="px-4 py-2 rounded-full text-sm bg-background-100 border border-background-200 text-foreground-700 hover:bg-primary-50 hover:border-primary-300 hover:text-primary-600 transition-all duration-200 whitespace-nowrap"
                >
                  <i className="ri-arrow-left-s-line w-4 h-4 flex items-center justify-center inline mr-1"></i>
                  上一话
                </Link>
              )}
              {hasNext && (
                <Link
                  to={`/comic/${comic.slug}/chapter/${chNum + 1}`}
                  className="px-4 py-2 rounded-full text-sm bg-primary-500 hover:bg-primary-600 text-background-50 transition-all duration-200 whitespace-nowrap"
                >
                  下一话
                  <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center inline ml-1"></i>
                </Link>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-[900px] mx-auto px-4 pb-12">
          <div className="flex flex-col items-center gap-2">
            {chapterContentImages.map((img, idx) => (
              <img
                key={idx}
                src={img}
                alt={`${comic.title} ${chapter.title} - 第${idx + 1}页`}
                title={`${comic.title} ${chapter.title}`}
                className="w-full h-auto rounded-lg"
                loading={idx < 3 ? 'eager' : 'lazy'}
              />
            ))}
          </div>

          <div className="mt-8 flex items-center justify-center gap-3">
            {hasPrev && (
              <Link
                to={`/comic/${comic.slug}/chapter/${chNum - 1}`}
                className="px-6 py-3 rounded-full text-sm bg-background-50 border border-background-200 text-foreground-700 hover:bg-primary-50 hover:border-primary-300 hover:text-primary-600 transition-all duration-200 whitespace-nowrap"
              >
                <i className="ri-arrow-left-s-line w-4 h-4 flex items-center justify-center inline mr-1"></i>
                上一话
              </Link>
            )}
            <Link
              to={`/comic/${comic.slug}`}
              className="px-5 py-3 rounded-full text-sm bg-foreground-950 hover:bg-foreground-800 text-background-50 transition-all duration-200 whitespace-nowrap"
            >
              返回目录
            </Link>
            {hasNext && (
              <Link
                to={`/comic/${comic.slug}/chapter/${chNum + 1}`}
                className="px-6 py-3 rounded-full text-sm bg-primary-500 hover:bg-primary-600 text-background-50 transition-all duration-200 whitespace-nowrap"
              >
                下一话
                <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center inline ml-1"></i>
              </Link>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}